import React from 'react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormData } from '../CareersApplicationForm';

interface ExperienceStepProps {
  formData: FormData;
  updateFormData: (data: Partial<FormData>) => void;
}

const ExperienceStep: React.FC<ExperienceStepProps> = ({ formData, updateFormData }) => {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Professional Experience</h3>
        <p className="text-sm text-gray-600 mb-6">Tell us about your professional background and experience.</p>
      </div>

      <div>
        <Label htmlFor="currentRole">Current Role/Position *</Label>
        <Input
          id="currentRole"
          value={formData.currentRole}
          onChange={(e) => updateFormData({ currentRole: e.target.value })}
          placeholder="e.g., Senior Software Engineer, Product Manager"
        />
      </div>

      <div>
        <Label htmlFor="experience">Years of Experience *</Label>
        <Select value={formData.experience} onValueChange={(value) => updateFormData({ experience: value })}>
          <SelectTrigger>
            <SelectValue placeholder="Select your experience level" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0-1">0-1 years</SelectItem>
            <SelectItem value="2-3">2-3 years</SelectItem>
            <SelectItem value="4-5">4-5 years</SelectItem>
            <SelectItem value="6-8">6-8 years</SelectItem>
            <SelectItem value="9-12">9-12 years</SelectItem>
            <SelectItem value="13+">13+ years</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="education">Education Background *</Label>
        <Textarea
          id="education"
          value={formData.education}
          onChange={(e) => updateFormData({ education: e.target.value })}
          placeholder="Describe your educational background, degrees, certifications, etc."
          rows={4}
        />
      </div>

      <div>
        <Label htmlFor="certifications">Relevant Certifications</Label>
        <Textarea
          id="certifications"
          value={formData.certifications}
          onChange={(e) => updateFormData({ certifications: e.target.value })}
          placeholder="List any relevant certifications you have obtained"
          rows={3}
        />
      </div>

      <div>
        <Label htmlFor="whyJoinUs">Why do you want to join 3 Halves Labs? *</Label>
        <Textarea
          id="whyJoinUs"
          value={formData.whyJoinUs}
          onChange={(e) => updateFormData({ whyJoinUs: e.target.value })}
          placeholder="Tell us what attracts you to our company and this role specifically..."
          rows={4}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="availabilityDate">Availability Date *</Label>
          <Input
            id="availabilityDate"
            type="date"
            value={formData.availabilityDate}
            onChange={(e) => updateFormData({ availabilityDate: e.target.value })}
          />
        </div>

        <div>
          <Label htmlFor="workPreference">Work Preference *</Label>
          <Select value={formData.workPreference} onValueChange={(value) => updateFormData({ workPreference: value })}>
            <SelectTrigger>
              <SelectValue placeholder="Select work preference" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="remote">Remote</SelectItem>
              <SelectItem value="hybrid">Hybrid</SelectItem>
              <SelectItem value="onsite">On-site</SelectItem>
              <SelectItem value="flexible">Flexible/Open to discuss</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label htmlFor="salaryExpectation">Salary Expectation (Optional)</Label>
        <Input
          id="salaryExpectation"
          value={formData.salaryExpectation}
          onChange={(e) => updateFormData({ salaryExpectation: e.target.value })}
          placeholder="e.g., $80,000 - $100,000 annually"
        />
      </div>

      <div>
        <Label htmlFor="coverLetter">Additional Information (Optional)</Label>
        <Textarea
          id="coverLetter"
          value={formData.coverLetter}
          onChange={(e) => updateFormData({ coverLetter: e.target.value })}
          placeholder="Any additional information you'd like to share with us..."
          rows={4}
        />
      </div>
    </div>
  );
};

export default ExperienceStep;